import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Calendar,
  Clock,
  FileText,
  BarChart3,
  Settings,
  Activity,
  Building2,
  ChevronDown,
  ChevronRight,
  User,
  LogOut,
  Menu,
  X,
  TestTube,
  Pill,
  DollarSign,
  Bed,
  Stethoscope,
  Shield,
  ShieldCheck,
  Receipt,
  FolderOpen,
  Image,
  HeartPulse,
  Heart,
  Scissors,
  Watch,
  Wifi,
  TrendingUp,
  FlaskConical,
  Clipboard,
  ClipboardList,
  LayoutDashboard,
  UserCog,
  Video,
  Upload,
  IndianRupee,
  ClipboardCheck,
  Bell,
  ShieldAlert,
  ListOrdered,
  Database,
  SlidersHorizontal,
  CreditCard,
  Sun,
  Moon,
  PanelLeftClose,
  PanelLeft,
  Wallet,
  MessageCircle,
  Megaphone,
  GitBranch,
  Star,
  UserPlus,
  Phone,
  Target,
  Package
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useAuth, getRoleDisplayName } from '@/context/AuthContext';
import { useSite } from '@/context/SiteContext';
import { NotificationBell } from '@/components/NotificationBell';
import { getRolePrefix } from '@/hooks/useRolePath';
import GlobalSearch from '@/components/GlobalSearch';
import PatientChatSidebar from '@/components/PatientChatSidebar';

/* ── Theme Toggle Helper ── */
const useTheme = () => {
  const [theme, setThemeState] = useState(() => {
    try {
      const settings = JSON.parse(localStorage.getItem('emr_user_settings') || '{}');
      return settings.theme || 'light';
    } catch { return 'light'; }
  });

  const setTheme = (t) => {
    setThemeState(t);
    document.documentElement.classList.toggle('dark', t === 'dark');
    try {
      const settings = JSON.parse(localStorage.getItem('emr_user_settings') || '{}');
      settings.theme = t;
      localStorage.setItem('emr_user_settings', JSON.stringify(settings));
    } catch { /* ignore */ }
  };

  return { theme, setTheme, toggleTheme: () => setTheme(theme === 'dark' ? 'light' : 'dark') };
};

export const TopBar = ({ onMenuToggle }) => {
  const { user, logout } = useAuth();
  const { sites, activeSite, switchSite } = useSite();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const rp = (path) => `/${getRolePrefix(user?.role)}${path}`;
  const activeSiteName = activeSite?.name || activeSite?.site_name || activeSite?.displayName || 'Primary clinic';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-50 shrink-0 border-b border-slate-300 bg-white shadow-[0_2px_10px_rgba(15,23,42,0.08)]">
      <div className="flex h-16 items-center justify-between gap-2 px-3 sm:px-4 lg:px-6">
        {/* Left — Logo + Sidebar Toggle */}
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden h-9 w-9 shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted"
            onClick={onMenuToggle}
          >
            <Menu className="h-5 w-5" />
          </Button>

          <div
            className="flex min-w-0 cursor-pointer items-center gap-2.5"
            onClick={() => navigate(rp('/dashboard'))}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && navigate(rp('/dashboard'))}
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary shadow-sm">
              <Activity className="h-5 w-5 text-primary-foreground" />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-base font-brand font-bold text-foreground leading-tight tracking-tight">
                Vivalyn<span className="font-normal text-primary">EMR</span>
              </h1>
              <p className="text-2xs text-muted-foreground leading-tight">AI Systems for Life & HealthCare</p>
            </div>
          </div>
        </div>

        {/* Center — Global Search (hidden for patient and superadmin roles) */}
        {user?.role !== 'patient' && user?.role !== 'superadmin' && <GlobalSearch />}

        {/* Right — Site · Theme · Notifications · User */}
        <div className="flex shrink-0 items-center gap-1 sm:gap-1.5">
          {/* Clinic / Site Context */}
          {sites.length > 1 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-9 gap-1.5 px-2 text-muted-foreground hover:text-foreground hover:bg-muted lg:px-3">
                  <Building2 className="h-4 w-4" />
                  <span className="hidden lg:inline text-xs font-medium max-w-[180px] truncate">{activeSiteName}</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs text-muted-foreground">Switch Facility</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {sites.map((site) => (
                  <DropdownMenuItem
                    key={site.id}
                    onClick={() => switchSite(site)}
                    className={activeSite?.id === site.id ? 'bg-primary/10 text-primary' : ''}
                  >
                    <div className="flex flex-col">
                      <span className="text-sm">{site.name}</span>
                      {site.code && <span className="text-2xs text-muted-foreground">{site.code}</span>}
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="hidden h-9 items-center gap-1.5 rounded-md px-2.5 text-muted-foreground lg:flex">
              <Building2 className="h-4 w-4" />
              <span className="max-w-[180px] truncate text-xs font-medium">{activeSiteName}</span>
            </div>
          )}

          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-muted"
            onClick={toggleTheme}
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>

          {/* Notifications */}
          <NotificationBell className="text-muted-foreground hover:text-foreground hover:bg-muted" />

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-9 gap-2 px-2 hover:bg-muted md:pl-2 md:pr-3">
                <Avatar className="h-7 w-7">
                  <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                    {user?.name ? user.name.split(' ').map(n => n[0]).join('') : 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden md:flex flex-col items-start">
                  <span className="text-xs font-medium leading-tight text-foreground">{user?.name || 'User'}</span>
                  <span className="text-2xs text-muted-foreground leading-tight">{getRoleDisplayName(user?.role)}</span>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-medium">{user?.name || 'User'}</span>
                  <span className="text-xs font-normal text-muted-foreground">{user?.email || ''}</span>
                  <Badge variant="secondary" className="text-2xs w-fit mt-0.5 bg-primary/10 text-primary border-0">
                    {getRoleDisplayName(user?.role)}
                  </Badge>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate(rp('/profile'))}>
                <User className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(rp('/settings'))}>
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export const SideNav = ({ isOpen, onClose, onCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { activeSite } = useSite();
  const [expandedSections, setExpandedSections] = useState(['OPD', 'IPD']);

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  // ─────────────────────────────────────────────────────────────
  // Navigation structure with RBAC-based visibility
  //
  // Each nav item has a `roles` array — the sidebar shows items
  // accessible to the user's role. SuperAdmin sees everything.
  //
  // Role categories:
  //   System:       tenantadmin, siteadmin, admin
  //   Clinical:     doctor, surgeon, anesthetist, nurse, headnurse,
  //                 pharmacist, labtechnician, radiologist,
  //                 physiotherapist, dentist
  //   Operational:  receptionist, billingstaff, medicalrecords, staff
  //   Admin:        hrmanager, itadmin, qualityofficer
  //   Patient:      patient
  // ─────────────────────────────────────────────────────────────

  // Helper: all clinical + admin roles that can access patient data
  const CLINICAL_ROLES = ['doctor', 'surgeon', 'anesthetist', 'nurse', 'headnurse', 'pharmacist', 'labtechnician', 'radiologist', 'physiotherapist', 'dentist'];
  const ADMIN_ROLES = ['tenantadmin', 'siteadmin', 'admin'];
  const ALL_NON_PATIENT = [...ADMIN_ROLES, ...CLINICAL_ROLES, 'receptionist', 'billingstaff', 'medicalrecords', 'staff', 'hrmanager', 'itadmin', 'qualityofficer'];

  const navigationGroups = [
    {
      id: 'main',
      label: null,
      items: [
        { icon: LayoutDashboard, label: 'Dashboard', path: user?.role === 'patient' ? '/dashboard' : ((['doctor', 'surgeon', 'dentist'].includes(user?.role)) ? '/doctor-hub' : '/dashboard'),
          roles: ['superadmin', ...ALL_NON_PATIENT, 'patient'] },
        { icon: Star, label: 'Feedback & Refills', path: '/patient-portal?tab=feedback', roles: ['patient'] },
        { icon: Heart, label: 'Health Summary', path: '/my-health-summary', roles: ['patient'] },
        { icon: MessageCircle, label: 'Messages', path: '/doctor-messages', roles: ['doctor', 'surgeon', 'dentist', 'physiotherapist'] },
        { icon: Clock, label: 'My Availability', path: '/my-availability',
          roles: ['doctor', 'surgeon', 'dentist'] },
        { icon: FileText, label: 'My Health Report', path: '/my-health-report', roles: ['patient'] },
        { icon: MessageCircle, label: 'My Messages', path: '/my-messages', roles: ['patient'] },
        { icon: ShieldAlert, label: 'My Vaccinations', path: '/my-vaccinations', roles: ['patient'], pediatricOnly: true },
        { icon: Calendar, label: 'My Visits', path: '/my-visits', roles: ['patient'] },
        { icon: Users, label: 'Patient List', path: '/patients',
          roles: ['doctor', 'surgeon', 'dentist', 'physiotherapist', 'nurse', 'headnurse', 'receptionist', 'staff'] },
        { icon: ListOrdered, label: 'Patient Queue', path: '/queue', roles: ['doctor', 'surgeon', 'dentist', 'physiotherapist', 'nurse', 'headnurse', 'receptionist', 'staff'] },
        { icon: ClipboardList, label: 'Register Patient', path: '/patient-intake',
          roles: ['doctor', 'surgeon', 'dentist', 'physiotherapist', 'nurse', 'headnurse', 'receptionist', 'staff'] },
        { icon: Video, label: 'Remote Monitoring', path: '/remote-monitoring', roles: ['doctor', 'surgeon', 'nurse', 'headnurse'] },
        { icon: Building2, label: 'Sites', path: '/sites', roles: ['superadmin', 'tenantadmin'] },
        { icon: Users, label: 'Users', path: '/users', roles: ['superadmin', 'tenantadmin'] },
      ]
    },
    {
      id: 'medscribe',
      label: 'MedScribe AI',
      collapsible: true,
      items: [
        { icon: IndianRupee, label: 'AI Billing', path: '/medscribe/billing', roles: ['doctor', 'surgeon', 'dentist'] },
        { icon: ShieldAlert, label: 'Clinical CDS', path: '/medscribe/cds', roles: ['doctor', 'surgeon', 'dentist'] },
        { icon: Bell, label: 'Follow-ups', path: '/medscribe/followup', roles: ['doctor', 'surgeon', 'dentist', 'nurse', 'headnurse'] },
      ]
    },
    {
      id: 'OPD',
      label: 'Outpatient (OPD)',
      collapsible: true,
      requiredService: 'OPD',
      items: [
        { icon: Clock, label: 'My Availability', path: '/my-availability',
          roles: ['physiotherapist'] },
      ]
    },
    {
      id: 'IPD',
      label: 'Inpatient (IPD)',
      collapsible: true,
      requiredService: 'IPD',
      items: [
        { icon: Bed, label: 'Bed Management', path: '/bed-management',
          roles: ['doctor', 'surgeon', 'nurse', 'headnurse', ...ADMIN_ROLES] },
        { icon: Bed, label: 'Inpatient Management', path: '/ipd',
          roles: ['doctor', 'surgeon', 'anesthetist', 'nurse', 'headnurse'] },
        { icon: HeartPulse, label: 'Nursing Station', path: '/nursing',
          roles: ['nurse', 'headnurse', 'doctor', 'surgeon'] },
        { icon: Scissors, label: 'OT & Procedures', path: '/ot',
          roles: ['doctor', 'surgeon', 'anesthetist'] },
      ]
    },
    {
      id: 'diagnostics',
      label: 'Diagnostics',
      collapsible: true,
      requiredServices: ['Lab', 'Radiology'],
      items: [
        { icon: TestTube, label: 'Laboratory (LIS)', path: '/laboratory',
          roles: ['doctor', 'surgeon', 'nurse', 'headnurse', 'labtechnician'],
          requiredService: 'Lab' },
        { icon: Image, label: 'Radiology (RIS)', path: '/radiology',
          roles: ['doctor', 'surgeon', 'radiologist'],
          requiredService: 'Radiology' },
      ]
    },
    {
      id: 'services',
      label: 'Services',
      collapsible: true,
      items: [
        { icon: BarChart3, label: 'Analytics', path: '/analytics',
          roles: ['superadmin', 'tenantadmin', 'receptionist', 'staff'] },
        { icon: FolderOpen, label: 'Documents', path: '/documents',
          roles: ['tenantadmin', 'siteadmin', 'doctor', 'surgeon', 'nurse', 'headnurse', 'receptionist', 'medicalrecords'] },
        { icon: Pill, label: 'Pharmacy', path: '/pharmacy',
          roles: ['pharmacist', 'doctor', 'surgeon', 'dentist', 'nurse', 'headnurse'],
          requiredService: 'Pharmacy' },
        { icon: Package, label: 'Inventory', path: '/inventory',
          roles: ['superadmin', 'siteadmin', 'tenantadmin', 'admin', 'nurse', 'headnurse', 'receptionist', 'staff', 'pharmacist', 'labtechnician', 'storekeeper'],
          requiredService: 'Inventory' },
        { icon: BarChart3, label: 'Inv. Analytics', path: '/inventory-analytics',
          roles: ['superadmin', 'siteadmin', 'tenantadmin', 'admin', 'nurse', 'headnurse', 'receptionist', 'staff', 'pharmacist', 'labtechnician', 'storekeeper'],
          requiredService: 'Inventory' },
        { icon: FileText, label: 'Reports', path: '/reports',
          roles: ['superadmin', 'tenantadmin', 'receptionist', 'billingstaff', 'staff'] },
      ]
    },
    {
      id: 'crm',
      label: 'Patient CRM',
      collapsible: true,
      items: [
        { icon: Megaphone, label: 'Campaigns', path: '/crm/campaigns',
          roles: ['tenantadmin', 'receptionist', 'billingstaff', 'staff', 'siteadmin'] },
        { icon: Megaphone, label: 'CRM Dashboard', path: '/crm',
          roles: ['tenantadmin', 'receptionist', 'billingstaff', 'staff', 'siteadmin'] },
        { icon: Activity, label: 'Engagement', path: '/crm/engagement',
          roles: ['tenantadmin', 'receptionist', 'billingstaff', 'staff', 'siteadmin'] },
        { icon: Star, label: 'Feedback & NPS', path: '/crm/feedback',
          roles: ['tenantadmin', 'receptionist', 'billingstaff', 'staff', 'siteadmin'] },
        { icon: MessageCircle, label: 'Inbox', path: '/crm/inbox',
          roles: ['tenantadmin', 'receptionist', 'billingstaff', 'staff', 'siteadmin'] },
        { icon: GitBranch, label: 'Journeys', path: '/crm/journeys',
          roles: ['tenantadmin', 'receptionist', 'billingstaff', 'staff', 'siteadmin'] },
        { icon: UserPlus, label: 'Leads', path: '/crm/leads',
          roles: ['tenantadmin', 'receptionist', 'billingstaff', 'staff', 'siteadmin'] },
        { icon: Target, label: 'Revenue', path: '/crm/revenue',
          roles: ['tenantadmin', 'receptionist', 'billingstaff', 'staff', 'siteadmin'] },
        { icon: Users, label: 'Segments', path: '/crm/segments',
          roles: ['tenantadmin', 'receptionist', 'billingstaff', 'staff', 'siteadmin'] },
        { icon: Phone, label: 'Telephony', path: '/crm/telephony',
          roles: ['tenantadmin', 'receptionist', 'billingstaff', 'staff', 'siteadmin'] },
        { icon: FileText, label: 'Templates', path: '/crm/templates',
          roles: ['tenantadmin', 'receptionist', 'billingstaff', 'staff', 'siteadmin'] },
      ]
    },
    {
      id: 'billing',
      label: 'Billing & Finance',
      collapsible: true,
      requiredService: 'Billing',
      items: [
        { icon: DollarSign, label: 'Billing', path: '/billing',
          roles: ['receptionist', 'billingstaff', ...ADMIN_ROLES] },
        { icon: Receipt, label: 'Claims', path: '/claims',
          roles: ['receptionist', 'billingstaff', ...ADMIN_ROLES] },
        { icon: BarChart3, label: 'Doctor Billing', path: '/doctor-billing',
          roles: ['doctor', 'surgeon', 'dentist', ...ADMIN_ROLES] },
        { icon: Wallet, label: 'Doctor Earnings', path: '/doctor-earnings',
          roles: ['doctor', 'surgeon', 'dentist', ...ADMIN_ROLES] },
        { icon: IndianRupee, label: 'Doctor Fees', path: '/doctor-fees',
          roles: ['doctor', 'surgeon', 'dentist', ...ADMIN_ROLES] },
        { icon: ShieldCheck, label: 'Insurance', path: '/insurance',
          roles: ['receptionist', 'billingstaff', ...ADMIN_ROLES] },
        { icon: TrendingUp, label: 'Revenue Reports', path: '/revenue-reports',
          roles: [...ADMIN_ROLES] },
      ]
    },

    {
      id: 'admin',
      label: 'Administration',
      items: [
        { icon: Shield, label: 'Admin Panel', path: '/admin-panel',
          roles: ['superadmin', ...ADMIN_ROLES, 'hrmanager', 'itadmin'] },
        { icon: Receipt, label: 'Billing Report', path: '/billing-consolidation',
          roles: ['superadmin'] },
        { icon: UserCog, label: 'Configuration', path: '/config',
          roles: ['superadmin', ...ADMIN_ROLES, 'itadmin'] },
        { icon: Stethoscope, label: 'Dept Templates', path: '/department-templates',
          roles: ['superadmin', ...ADMIN_ROLES] },
        { icon: Database, label: 'Platform Master Data', path: '/platform-master-data',
          roles: ['superadmin'] },
        { icon: SlidersHorizontal, label: 'Platform Settings', path: '/platform-settings',
          roles: ['superadmin', ...ADMIN_ROLES, 'itadmin'] },
        { icon: CreditCard, label: 'Subscriptions', path: '/subscription-management',
          roles: ['superadmin'] },
      ]
    },
  ];

  // Filter navigation items based on user roles, site services, and add role prefix to paths
  const getFilteredGroups = () => {
    // Use the primary role property as the single source of truth
    const userRoles = user?.role ? [user.role.toLowerCase()] : [];
    const rolePrefix = `/${getRolePrefix(user?.role)}`;
    const siteServices = activeSite?.services || [];
    // If no services configured, show all groups (backward compatible)
    const hasServiceConfig = siteServices.length > 0;

    // Calculate patient age for pediatric filtering
    const patientAge = (() => {
      if (!user?.dateOfBirth) return null;
      const dob = new Date(user.dateOfBirth);
      if (isNaN(dob.getTime())) return null;
      return Math.floor((Date.now() - dob.getTime()) / 31557600000);
    })();

    if (process.env.NODE_ENV === 'development') {
      console.debug('[SideNav] user.role:', user?.role, '| resolved rolePrefix:', rolePrefix, '| siteServices:', siteServices);
    }

    return navigationGroups.map(group => {
      // Check group-level service requirement
      if (hasServiceConfig && group.requiredService && !siteServices.includes(group.requiredService)) {
        return { ...group, items: [] };
      }
      if (hasServiceConfig && group.requiredServices && !group.requiredServices.some(s => siteServices.includes(s))) {
        return { ...group, items: [] };
      }

      return {
        ...group,
        items: group.items
          .filter(item => {
            if (userRoles.length === 0) return false;
            if (!item.roles.some(r => userRoles.includes(r))) return false;
            // Check item-level service requirement
            if (hasServiceConfig && item.requiredService && !siteServices.includes(item.requiredService)) return false;
            // Pediatric-only items: show only if patient age <= 12
            if (item.pediatricOnly && (patientAge === null || patientAge > 12)) return false;
            return true;
          })
          .map(item => ({
            ...item,
            path: `${rolePrefix}${item.path}`,
          }))
      };
    }).filter(group => group.items.length > 0);
  };

  const filteredGroups = getFilteredGroups();
  const navItemClass = (isActive, highlight = false, prominent = false) => [
    'group relative w-full rounded-lg text-left text-[13px] font-medium transition-all duration-150',
    'flex items-center gap-2.5',
    prominent ? 'px-3 py-2.5' : 'px-3 py-2',
    isActive
      ? 'bg-cyan-400/20 text-white shadow-sm ring-1 ring-cyan-200/30'
      : highlight
      ? 'text-cyan-100 hover:bg-white/10 hover:text-white'
      : 'text-slate-200 hover:bg-white/10 hover:text-white'
  ].join(' ');
  const navIconClass = (isActive) => `h-4 w-4 flex-shrink-0 ${isActive ? 'text-cyan-200' : 'text-slate-300 group-hover:text-white'}`;

  const handleNavigation = (path) => {
    navigate(path);
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  return (
    <>
      {/* Overlay for mobile */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{ x: isOpen ? 0 : -280 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="fixed bottom-0 left-0 top-16 z-40 flex h-[calc(100dvh-4rem)] w-[var(--sidebar-width)] flex-col overflow-hidden border-r border-[#1f4652] bg-[#0f2f3a] shadow-sm backdrop-blur lg:sticky lg:translate-x-0"
      >
        {/* Close button for mobile */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 lg:hidden h-8 w-8 text-white/80 hover:bg-white/10 hover:text-white"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>

        {/* User Role Badge — mobile only */}
        <div className="border-b border-white/10 px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-300">
              Navigation
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="hidden h-8 w-8 shrink-0 text-slate-300 hover:bg-white/10 hover:text-white lg:inline-flex"
              onClick={onCollapse}
              title="Collapse side menu"
              aria-label="Collapse side menu"
            >
              <PanelLeftClose className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <nav className="custom-scrollbar min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain px-3 py-3">
          {filteredGroups.map((group, groupIdx) => (
            <div key={group.id} className={groupIdx > 0 ? 'mt-4' : ''}>
              {group.label ? (
                group.collapsible ? (
                  <Collapsible
                    open={expandedSections.includes(group.id)}
                    onOpenChange={() => toggleSection(group.id)}
                  >
                    <CollapsibleTrigger className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-300 transition-colors hover:bg-white/10 hover:text-white">
                      <span className="truncate">{group.label}</span>
                      <ChevronRight className={`h-3.5 w-3.5 transition-transform duration-200 ${expandedSections.includes(group.id) ? 'rotate-90 text-cyan-100' : ''}`} />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-1 space-y-1">
                      {group.items.map((item) => {
                        const isActive = location.pathname === item.path;
                        const Icon = item.icon;

                        return (
                          <button
                            key={item.path}
                            onClick={() => handleNavigation(item.path)}
                            className={navItemClass(isActive, item.highlight)}
                          >
                            {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r-full bg-cyan-300" />}
                            <Icon className={navIconClass(isActive)} />
                            <span className="truncate">{item.label}</span>
                            {item.highlight && !isActive && (
                              <span className="ml-auto flex h-1.5 w-1.5 rounded-full bg-cyan-300 animate-pulse-soft" />
                            )}
                          </button>
                        );
                      })}
                    </CollapsibleContent>
                  </Collapsible>
                ) : (
                  <>
                    <p className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-wider text-slate-300">
                      {group.label}
                    </p>
                    {group.items.map((item) => {
                      const isActive = location.pathname === item.path;
                      const Icon = item.icon;

                      return (
                        <button
                          key={item.path}
                          onClick={() => handleNavigation(item.path)}
                          className={navItemClass(isActive)}
                        >
                          {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r-full bg-cyan-300" />}
                          <Icon className={navIconClass(isActive)} />
                          <span className="truncate">{item.label}</span>
                        </button>
                      );
                    })}
                  </>
                )
              ) : (
                group.items.map((item) => {
                  const isActive = location.pathname === item.path;
                  const Icon = item.icon;

                  return (
                    <button
                      key={item.path}
                      onClick={() => handleNavigation(item.path)}
                      className={navItemClass(isActive, false, true)}
                    >
                      {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r-full bg-cyan-300" />}
                      <Icon className={navIconClass(isActive)} />
                      <span className="truncate">{item.label}</span>
                    </button>
                  );
                })
              )}
            </div>
          ))}
        </nav>

        <div className="mt-auto border-t border-white/10 px-3 py-2">
          <div className="h-1" />
        </div>

      </motion.aside>
    </>
  );
};

const AppLayout = () => {
  const [sideNavOpen, setSideNavOpen] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isPatient = user?.role?.toLowerCase() === 'patient';

  const breadcrumbs = location.pathname
    .split('/')
    .filter(Boolean)
    .slice(1)
    .map((segment) => segment
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase()));

  // Auto-close sidebar on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSideNavOpen(true);
      } else {
        setSideNavOpen(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background">
      <TopBar
        onMenuToggle={() => setSideNavOpen(!sideNavOpen)}
      />

      <div className="flex min-h-0 flex-1">
        {!sidebarCollapsed && (
          <SideNav
            isOpen={sideNavOpen}
            onClose={() => setSideNavOpen(false)}
            onCollapse={() => setSidebarCollapsed(true)}
          />
        )}
        {sidebarCollapsed && (
          <aside className="sticky top-16 hidden h-[calc(100dvh-4rem)] w-14 shrink-0 border-r border-[#1f4652] bg-[#0f2f3a] px-2 py-3 shadow-sm lg:flex lg:flex-col lg:items-center">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-white/70 hover:bg-white/10 hover:text-white"
              onClick={() => setSidebarCollapsed(false)}
              title="Expand side menu"
              aria-label="Expand side menu"
            >
              <PanelLeft className="h-4 w-4" />
            </Button>
          </aside>
        )}

        <main className="custom-scrollbar flex h-[calc(100dvh-4rem)] flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-contain bg-[#f6f8fb]">
          <div className="mx-auto w-full max-w-content flex-1 p-4 lg:p-5">
            {breadcrumbs.length > 0 && (
              <div className="mb-3 flex min-h-7 flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                <button
                  type="button"
                  className="font-medium text-primary hover:underline"
                  onClick={() => navigate(`/${getRolePrefix(user?.role)}/dashboard`)}
                >
                  EMR
                </button>
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={`${crumb}-${index}`}>
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/60" />
                    <span className={index === breadcrumbs.length - 1 ? 'font-medium text-foreground' : ''}>
                      {crumb}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            )}
            <Outlet />
          </div>

          <div className="mt-auto border-t border-slate-200/80 bg-white/70 px-4 py-2 backdrop-blur-sm lg:px-5">
            <div className="h-1" />
          </div>
        </main>
      </div>

      {isPatient && (
        <PatientChatSidebar patientId={user?.patientId || user?.id} patientName={user.name || 'Patient'} />
      )}
    </div>
  );
};

export default AppLayout;
